export const ACCOUNT_ACTIVITY_ENABLED =
  process.env.NEXT_PUBLIC_ACCOUNT_ACTIVITY_ENABLED === 'true'

export const ACCOUNT_RECOVERY_ENABLED =
  process.env.NEXT_PUBLIC_ACCOUNT_RECOVERY_ENABLED === 'true'

export type RecoveryResult =
  | 'recovered'
  | 'unavailable'
  | 'pending'
  | 'temporary'

type ApiOutputs = { custom?: { API?: Record<string, { endpoint?: unknown }> } }

export function getAccountApiEndpoint(outputs: ApiOutputs): string {
  const apis = Object.entries(outputs.custom?.API ?? {}).filter(
    ([name, value]) =>
      name.startsWith('skopApi-') && typeof value.endpoint === 'string',
  )
  // Never silently select another environment when outputs are ambiguous.
  if (apis.length !== 1) throw new Error('Account API is not configured')
  const endpoint = new URL(apis[0][1].endpoint as string)
  if (endpoint.protocol !== 'https:')
    throw new Error('Account API is not configured')
  return endpoint.toString().replace(/\/$/, '')
}

export async function postAccountCommand(
  endpoint: string,
  path: string,
  body: Record<string, string>,
  token?: string,
): Promise<Response> {
  return fetch(`${endpoint}${path}`, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
}

export function readRecoveryToken(fragment: string): string {
  const token =
    new URLSearchParams(fragment.replace(/^#/, '')).get('token') ?? ''
  return token.length <= 2048 ? token : ''
}

export async function confirmAccountRecovery(
  endpoint: string,
  token: string,
): Promise<RecoveryResult> {
  const response = await postAccountCommand(
    endpoint,
    '/account/recovery/confirm',
    { token },
  )
  if (response.status === 400) return 'unavailable'
  if (response.status === 409) return 'pending'
  if (!response.ok) return 'temporary'
  const result = await response.json()
  return result?.status === 'recovered' &&
    result.requiresAuthentication === true
    ? 'recovered'
    : 'temporary'
}

// Browser deduplication only limits traffic. Identity, time and authoritative
// deduplication belong to Data; no browser timestamp is sent to the server.
export function createActivityReporter(
  send: () => Promise<void>,
  now = Date.now,
  successIntervalMs?: number,
) {
  let nextAttemptAt = 0
  let inFlight: Promise<void> | undefined
  return () => {
    if (inFlight) return inFlight
    if (now() < nextAttemptAt) return Promise.resolve()
    const attemptedAt = now()
    nextAttemptAt = attemptedAt + 60_000
    inFlight = send()
      .then(() => {
        // Activity POSTs deduplicate by UTC day; status reads keep their
        // shorter foreground refresh window. Neither cadence starts a timer.
        nextAttemptAt =
          successIntervalMs === undefined
            ? (Math.floor(attemptedAt / 86_400_000) + 1) * 86_400_000
            : now() + successIntervalMs
      })
      .catch(() => {
        /* Best effort: never interrupt the user's session. */
      })
      .finally(() => {
        inFlight = undefined
      })
    return inFlight
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export const ACCOUNT_STATUS_ENABLED =
  process.env.NEXT_PUBLIC_ACCOUNT_STATUS_ENABLED === 'true'
export const accountStatusKey = (userId?: string) =>
  ['account-lifecycle', 'status', userId] as const
export type AccountStatus = {
  state: 'active' | 'notice' | 'inactive' | 'closed'
  deadlineAt: string | null
  deadlineKind: 'suspension' | 'closure' | null
  recoveryEligible: boolean
}

export function parseAccountStatus(value: unknown): AccountStatus {
  if (!isRecord(value)) throw new Error('Account status unavailable')
  const { state, deadlineAt, deadlineKind, recoveryEligible } = value
  if (
    (state !== 'active' &&
      state !== 'notice' &&
      state !== 'inactive' &&
      state !== 'closed') ||
    (deadlineKind !== null &&
      deadlineKind !== 'suspension' &&
      deadlineKind !== 'closure') ||
    (deadlineAt !== null &&
      (typeof deadlineAt !== 'string' ||
        !Number.isFinite(Date.parse(deadlineAt)))) ||
    (deadlineAt === null) !== (deadlineKind === null) ||
    typeof recoveryEligible !== 'boolean'
  )
    throw new Error('Account status unavailable')
  return { state, deadlineAt, deadlineKind, recoveryEligible }
}

export async function fetchAccountStatus(
  endpoint: string,
  token: string,
): Promise<AccountStatus> {
  const response = await fetch(`${endpoint}/account/status`, {
    method: 'GET',
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    headers: { Authorization: `Bearer ${token}` },
  })
  // This informational read must never clear a session or an in-progress form.
  if (!response.ok) throw new Error('Account status unavailable')
  return parseAccountStatus(await response.json())
}
